import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/f853c01ba15294f11fa2de10e5a0ae4f.jpg"
          alt="Hero Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/3294a4af19582454411e375cbd1c78be/a19a10601520d1fc990e87845b2a9082.png"
                  alt="PBPKNM Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className={`${isScrolled ? 'text-gray-800' : 'text-white'}`}>
                <div className="text-base sm:text-lg font-bold">PBPKNM</div>
                <div className="text-xs sm:text-sm opacity-90 hidden sm:block">Kempen Ramadhan & Raya</div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="#pengenalan" className={`text-sm font-semibold hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                Pengenalan
              </a>
              <a href="#matlamat" className={`text-sm font-semibold hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                Matlamat
              </a>
              <a href="#pakej" className={`text-sm font-semibold hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                Pakej
              </a>
              <a href="#sasaran" className={`text-sm font-semibold hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                Sasaran
              </a>
              <a href="#infaq" className={`text-sm font-semibold hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                Infaq
              </a>
              <a href="#hubungi" className={`text-sm font-semibold hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                Hubungi
              </a>
            </div>

            {/* CTA Button */}
            <a 
              href="#infaq"
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-bold rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span className="hidden sm:inline">Infaq Sekarang</span>
              <span className="sm:hidden">Infaq</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="text-center text-white">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8 border border-white/30">
              <i className="ri-moon-line"></i>
              <span className="hidden sm:inline">Ramadhan Al-Mubarak 1446H</span>
              <span className="sm:hidden">Ramadhan 1446H</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight px-4">
              Kempen Infaq Bakul Makanan<br className="hidden sm:block" />
              <span className="text-amber-300">Ramadhan & Hari Raya</span>
            </h1>

            {/* Subheading */}
            <p className="text-base sm:text-lg md:text-xl text-emerald-100 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
              Membantu bekas anggota pasukan keselamatan negara melalui agihan bakul makanan sempena bulan Ramadhan dan sambutan Hari Raya Aidilfitri
            </p>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-4xl mx-auto mb-8 sm:mb-12 px-4">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20">
                <div className="text-3xl sm:text-4xl font-bold text-amber-300 mb-2">5,000</div>
                <div className="text-xs sm:text-sm text-emerald-100">Bakul Sasaran</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20">
                <div className="text-3xl sm:text-4xl font-bold text-amber-300 mb-2">RM150</div>
                <div className="text-xs sm:text-sm text-emerald-100">Nilai Setiap Bakul</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20">
                <div className="text-3xl sm:text-4xl font-bold text-amber-300 mb-2">Seluruh</div>
                <div className="text-xs sm:text-sm text-emerald-100">Malaysia</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
              <a 
                href="#infaq"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm sm:text-base font-bold rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-hand-heart-line text-lg sm:text-xl"></i>
                  Sumbang Sekarang
                </span>
              </a>
              <a 
                href="#pengenalan"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white/20 backdrop-blur-sm text-white text-sm sm:text-base font-bold rounded-xl border-2 border-white/30 hover:bg-white/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-information-line text-lg sm:text-xl"></i>
                  Ketahui Lebih Lanjut
                </span>
              </a>
            </div>

            {/* Scroll Indicator */}
            <div className="mt-12 sm:mt-16 animate-bounce">
              <i className="ri-arrow-down-line text-2xl sm:text-3xl text-white/80"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
